import type { PiralPlugin } from 'piral-core'
import type { DomainConfiguration } from './types'

export type { DomainConfiguration } from './types'

/**
 * The domain configuration endpoint. Same-origin, like `whoami`: the shell and the
 * content API share this host, so the request carries the session cookie with no
 * `credentials`/CORS handling. GET, so it needs no CSRF token itself — it is where
 * the token every *other* request needs comes from.
 */
const DOMAIN_CONFIGURATION_URL = '/api/3.0/content/configuration/domain'

/**
 * The single in-flight (then settled) domain-configuration request, shared by every
 * consumer in the shell. Created lazily on the first call and reused thereafter, so
 * the pilet API ({@link createConfigApi}) and the shell's own code (device
 * registration in `AuthProvider`, which needs the CSRF token) all await one fetch
 * rather than each issuing their own.
 */
let configuration: Promise<DomainConfiguration> | undefined

/**
 * Load the domain configuration once and hand the same promise to every caller. The
 * shell uses this directly when it needs the session CSRF token outside a pilet
 * (e.g. registering the device right after `whoami`); pilets reach the same value
 * through `getDomainConfiguration` on their API.
 */
export function loadDomainConfiguration (): Promise<DomainConfiguration> {
  if (!configuration) {
    configuration = fetch(DOMAIN_CONFIGURATION_URL, {
      headers: { Accept: 'application/json' }
    }).then(res => {
      if (!res.ok) throw new Error(`Domain configuration request failed: ${res.status}`)
      return res.json() as Promise<DomainConfiguration>
    })
  }
  return configuration
}

export interface PiletConfigApi {
  /**
   * The shell's domain configuration, fetched once at startup and shared with every
   * pilet. Resolves to the same cached value on every call, and rejects if that one
   * fetch failed. Its `csrf_token` is required on every non-GET content-API request
   * (attach it as the root-level `__csrfToken` body field).
   */
  getDomainConfiguration (): Promise<DomainConfiguration>
}

/**
 * Piral plugin that loads the domain configuration once at shell startup and puts
 * `getDomainConfiguration` on every pilet's API.
 *
 * The shell owns this fetch — the configuration (and its CSRF token) is stable for
 * the page's lifetime and shared across pilets, so it belongs here rather than
 * being re-fetched by each pilet that needs the token. The promise is created once
 * (when the plugin is installed, i.e. at `createInstance`) and handed out as-is, so
 * every consumer awaits the same in-flight or settled request.
 */
export function createConfigApi (): PiralPlugin<PiletConfigApi> {
  const configuration = loadDomainConfiguration()

  return () => ({
    getDomainConfiguration () {
      return configuration
    }
  })
}
